"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { LayoutDashboard, LogOut, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import type { CurrentUser } from "@plexstore/types";

import { UserAvatar } from "@/components/shared/user-avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { errorMessage } from "@/lib/api/errors";
import { authService } from "@/services/client";

export function UserMenu({ user }: { user: CurrentUser }) {
  const router = useRouter();
  const logout = useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      toast.success("Signed out");
      router.push("/");
      router.refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-xl p-1 pr-3 transition hover:bg-white/5 focus-visible:outline-none">
        <UserAvatar name={user.displayName} src={user.avatarUrl} size={32} />
        <span className="hidden max-w-[120px] truncate text-sm font-medium md:inline">{user.displayName}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="truncate">{user.email ?? user.displayName}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href={`/profile/${user.publicId}`}>
            <UserIcon /> Profile
          </Link>
        </DropdownMenuItem>
        {user.permissions && (
          <DropdownMenuItem asChild>
            <Link href="/staff/overview">
              <LayoutDashboard /> Staff panel
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => logout.mutate()} disabled={logout.isPending}>
          <LogOut /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
